// Hideo - script editor, Fountain mode: the writing helpers, without UI (FountainEditor.vue applies them to its text
// field). The element of the line with the cursor, Tab / Shift+Tab to change it (the line's Fountain marks, capitals
// and the empty line before it are rewritten), the element that follows on Enter, and the suggestions (character
// names, INT./EXT., places, times of day, transitions: the standard English screenplay terms, as in every screenplay).
// Tests: tests/fountain.test.ts.
import { SCENE_RE, fountainCharacters, fountainPlaces, isCapsName, splitHeading, type FountainDoc, type FountainKind } from './fountain'

/** Elements the writer moves between with Tab (Shift+Tab goes back) */
export const CYCLE: readonly FountainKind[] = ['action', 'character', 'parenthetical', 'dialogue', 'transition', 'scene']
/** Every element that can be chosen from the menu, in its order */
export const ELEMENTS: readonly FountainKind[] = ['scene', 'action', 'character', 'parenthetical', 'dialogue', 'transition', 'centered', 'lyrics', 'section', 'synopsis', 'note']
/** Scene heading prefixes, times of day, character extensions and transitions */
export const SCENE_PREFIXES = ['INT.', 'EXT.', 'INT./EXT.', 'EST.', 'I/E.']
export const TIMES_OF_DAY = ['DAY', 'NIGHT', 'MORNING', 'AFTERNOON', 'EVENING', 'DAWN', 'DUSK', 'CONTINUOUS', 'LATER', 'MOMENTS LATER', 'SAME TIME']
export const EXTENSIONS = ['V.O.', 'O.S.', 'O.C.', "CONT'D"]
export const TRANSITIONS = ['CUT TO:', 'DISSOLVE TO:', 'SMASH CUT TO:', 'MATCH CUT TO:', 'JUMP CUT TO:', 'FADE OUT.', 'FADE TO BLACK.']

/** Elements written after an empty line (all but the parts of a speech) */
const AFTER_BLANK: ReadonlySet<FountainKind> = new Set(['scene', 'action', 'character', 'transition', 'centered', 'section', 'synopsis', 'note'])

/** The line around a position: its number, where it starts and ends, its text */
export interface LineInfo { index: number; start: number; end: number; text: string }
export function lineAt(src: string, pos: number): LineInfo {
  const start = src.lastIndexOf('\n', pos - 1) + 1, e = src.indexOf('\n', pos), end = e < 0 ? src.length : e
  return { index: src.slice(0, start).split('\n').length - 1, start, end, text: src.slice(start, end) }
}
/** Where line n starts */
export function lineStart(src: string, n: number): number {
  let p = 0
  for (let k = 0; k < n; k++) { const e = src.indexOf('\n', p); if (e < 0) return src.length; p = e + 1 }
  return p
}

/** The element of every source line (null = empty line or title page) */
export function lineKinds(doc: FountainDoc, count: number): (FountainKind | null)[] {
  const out: (FountainKind | null)[] = new Array(count).fill(null)
  doc.tokens.forEach(tk => { if (tk.kind !== 'boneyard') for (let k = 0; k < tk.lines && tk.line + k < count; k++) out[tk.line + k] = tk.kind })
  return out
}

/** The element of the line with the cursor. A line being written reads as what it will be: an empty line under a
 *  character cue or a parenthetical is dialogue; a line in capitals after an empty line, with nothing under it yet, is a
 *  character cue (it becomes one when its dialogue is written). `pending` = the element chosen for this empty line */
export function caretKind(src: string, doc: FountainDoc, line: number, pending: FountainKind | null): FountainKind {
  if (pending) return pending
  const L = src.split('\n'), kinds = lineKinds(doc, L.length), text = (L[line] || '').trim()
  const prev = line > 0 ? kinds[line - 1] : null, prevBlank = line === 0 || !L[line - 1].trim()
  if (!text) return !prevBlank && (prev === 'character' || prev === 'parenthetical' || prev === 'dialogue') ? 'dialogue' : 'action'
  const k = kinds[line] || 'action'
  if (k === 'action' && prevBlank && !(L[line + 1] || '').trim() && /^[^!]/.test(text) && isCapsName(text) && !/TO:$/.test(text)) return 'character'
  return k
}

/** A line's text without its Fountain marks (. ! @ > < ~ = # [[ ]] ( ) and the ^ of dual dialogue) */
export function stripMarks(line: string): string {
  let t = line.trim()
  if (/^\[\[.*\]\]$/.test(t)) return t.slice(2, -2).trim()
  t = t.replace(/^(#+|=(?!==)|~|!|@|>|\.(?!\.))\s*/, '').replace(/\s*<$/, '').replace(/\s*\^$/, '')
  if (/^\(.*\)$/.test(t)) t = t.slice(1, -1).trim()
  return t
}
/** A text written as an element: the marks and capitals it needs to read as that element in Fountain */
export function asKind(text: string, kind: FountainKind): string {
  const t = text.trim(), up = t.toUpperCase()
  switch (kind) {
    case 'scene': return !t ? '' : SCENE_RE.test(up) ? up : '.' + up
    case 'character': return !t ? '' : isCapsName(up) ? up : '@' + t
    case 'parenthetical': return '(' + t + ')'
    case 'transition': return !t ? '' : /TO:$/.test(up) ? up : '> ' + up
    case 'centered': return '> ' + t + ' <'
    case 'lyrics': return '~' + t
    case 'section': return '# ' + t
    case 'synopsis': return '= ' + t
    case 'note': return '[[' + t + ']]'
    case 'action': return t && (SCENE_RE.test(t) || /^[.!@>~=#]/.test(t) || isCapsName(t)) ? '!' + t : t
    default: return t
  }
}

/** A change of the text: replace start…end with `text`, then the cursor at `caret` */
export interface TextEdit { start: number; end: number; text: string; caret: number }

/** Line n turned into an element: its text rewritten (asKind) and the empty line before it added (scene heading,
 *  character, transition, action…) or removed (dialogue and parenthetical go right under the cue). The cursor goes at
 *  the end of the text (inside the brackets of a parenthetical) */
export function convertLine(src: string, n: number, kind: FountainKind): TextEdit {
  const L = src.split('\n'), cur = L[n] ?? '', base = stripMarks(cur)
  const text = asKind(base, kind)
  let start = lineStart(src, n), prefix = ''
  const end = start + cur.length
  if (n > 0 && AFTER_BLANK.has(kind) && L[n - 1].trim()) prefix = '\n'
  if (!AFTER_BLANK.has(kind) && n > 1 && !L[n - 1].trim() && L[n - 2].trim()) start = lineStart(src, n - 1)   // the empty line goes
  const caret = start + prefix.length + text.length - (kind === 'parenthetical' ? 1 : kind === 'centered' ? 2 : kind === 'note' ? 2 : 0)
  return { start, end, text: prefix + text, caret }
}
/** The next (dir 1) or previous (dir -1) element of the Tab cycle; an element outside the cycle starts from Action */
export function cycleKind(kind: FountainKind, dir: 1 | -1): FountainKind {
  const i = CYCLE.indexOf(kind)
  return CYCLE[((i < 0 ? 0 : i) + dir + CYCLE.length) % CYCLE.length]
}
/** What Enter starts after an element, and the line breaks it takes (one under a cue, two otherwise) */
export function nextKind(kind: FountainKind): { kind: FountainKind; breaks: string } {
  const next: Partial<Record<FountainKind, FountainKind>> = { scene: 'action', character: 'dialogue', parenthetical: 'dialogue', dialogue: 'character', transition: 'scene', section: 'scene' }
  const k = next[kind] || 'action'
  return { kind: k, breaks: k === 'dialogue' || k === 'parenthetical' ? '\n' : '\n\n' }
}

/** A suggestion: what is shown, and the whole line it writes */
export interface Suggestion { label: string; line: string }
/** Suggestions for the line being written: character names already used (and the extensions after "("), scene
 *  heading prefixes, places already used and times of day, transitions. At most 8; none when the line is already one */
export function suggestions(doc: FountainDoc, kind: FountainKind, line: string): Suggestion[] {
  const up = line.trim().toUpperCase().replace(/^[@.>]\s*/, '')
  const pick = (list: string[], typed: string, make: (s: string) => string) =>
    list.filter(s => s.startsWith(typed) && s !== typed).slice(0, 8).map(s => ({ label: s, line: make(s) }))
  if (kind === 'character') {
    const i = up.indexOf('(')
    if (i >= 0) { const name = up.slice(0, i).trim(), ext = up.slice(i + 1).replace(/\)$/, '').trim(); return pick(EXTENSIONS, ext, e => `${name} (${e})`) }
    return pick(fountainCharacters(doc).map(c => c.name), up, s => s)
  }
  if (kind === 'scene') {
    const pre = SCENE_PREFIXES.find(p => up.startsWith(p + ' ')) || (SCENE_RE.test(up) ? splitHeading(up).prefix : '')
    if (!pre || !up.startsWith(pre + ' ')) return up.includes(' ') && !pre ? [] : pick(SCENE_PREFIXES, up, p => p + ' ')
    const rest = up.slice(pre.length + 1), dash = rest.lastIndexOf(' - ')
    if (dash < 0) return pick(fountainPlaces(doc), rest.trim(), p => `${pre} ${p} - `)
    return pick(TIMES_OF_DAY, rest.slice(dash + 3).trim(), d => `${pre} ${rest.slice(0, dash)} - ${d}`)
  }
  if (kind === 'transition') return pick(TRANSITIONS, up, s => asKind(s, 'transition'))
  return []
}
